// editor-utils.js

import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import ButtonOptionsModal from '../actions/button-options-modal'
import SelectedIcon from 'src/components/selected-icon'
import {
  handleAddEmbed,
  handleAddImageHotspot,
  handleAddInterActive,
  handleAddInterActiveFreeAccount,
  handleAddCommunityBoard,
  handleAddCommunityBoardFreeAccount,
} from 'src/components/editor/utils'
import BlockOptionsModal from '../simple-box-wrapper-options/block-options'
import HeaderSettings from '../../../../landing-page/landing-page-editor/components/landing-page-header/header-settings'
import { ButtonActionType, ButtonData, ButtonType } from 'src/model/button'
import { patchCourseLandingPageAction } from 'src/reducers/course/action'
import { patchCollectionLandingPageAction } from 'src/reducers/collection/action'
import UserType from 'src/model/user-types'
import { track } from '@amplitude/analytics-browser'
import URL from 'src/api/url'
import TabOptionsModals from '../actions/tab-options-modals'
import HotspotSettingsModal from '../actions/hotspot-settings-modal'
import { editorEnum } from 'src/model/editor/editorEnum'
import {
  processStartAction,
  processStartEnd,
} from 'src/reducers/process/action'
import { snackbarShowAction } from 'src/reducers/snackbar/action'
import SnackbarMessage from 'src/model/snackbar'
import AiActivityPopup from 'src/components/ai-activity-popup'
import { generateActivityService } from 'src/api/course'
import { headerBackgroundCss } from 'src/utils/default-header-background'

// UUID generator function
export const generateUUID = () => {
  // Try modern crypto API first
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback to manual UUID generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Default game config for Pixel Quest
const DEFAULT_PIXEL_QUEST_CONFIG = {
  title: 'The Path of the Product Bridge',
  mapGrid: {
    _hide: true,
    _value: [
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      3,
      3,
      3,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      3,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      3,
      3,
      3,
      3,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      3,
      1,
      1,
      1,
      1,
      1,
      3,
      3,
      3,
      3,
      3,
      1,
      1,
      1,
      1,
      1,
      3,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      3,
      3,
      3,
      3,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      3,
      3,
      9,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
      1,
    ],
  },
  startPosition: {
    _hide: true,
    _value: { x: 1, y: 1 },
  },
  config: {
    language: 'en',
    pathColor: '#374151',
    wallColor: '#111827',
    bgColor: '#020617',
    titleColor: '#facc15',
    exitText: 'EXIT',
    instructionText: 'ANSWER ALL MASTERS CORRECTLY TO UNLOCK THE EXIT',
    victoryText: 'QUEST COMPLETE!',
    gameOverText: 'GAME OVER',
    tryAgainText: 'TRY AGAIN',
  },
  npcs: [
    {
      id: 1,
      name: 'The Architect',
      spriteColor: '#3b82f6',
      position: { x: 3, y: 1 },
      question:
        'Product Management acts as a bridge between which two historically misaligned groups?',
      options: {
        _type: 'radio',
        _options: [
          'Investors and Competitors',
          'Engineers and Business Leaders',
          'Project Managers and Designers',
          'HR and Marketing',
        ],
        _value: 'Engineers and Business Leaders',
      },
    },
    {
      id: 2,
      name: 'The Intersection Sage',
      spriteColor: '#ef4444',
      position: { x: 5, y: 3 },
      question:
        "According to the Intersection Model, what happens if you ignore the 'Technology' domain?",
      options: {
        _type: 'radio',
        _options: [
          'The company goes bankrupt',
          'Nobody uses the product',
          'The product cannot be built',
          'The product has no value proposition',
        ],
        _value: 'The product cannot be built',
      },
    },
    {
      id: 3,
      name: 'The Lean Strategist',
      spriteColor: '#10b981',
      position: { x: 4, y: 5 },
      question:
        'What is the primary purpose of a Minimum Viable Product (MVP)?',
      options: {
        _type: 'radio',
        _options: [
          'To release a half-baked product quickly',
          'To save money on development costs',
          'To collect maximum validated learning with least effort',
          'To provide all features to early adopters',
        ],
        _value: 'To collect maximum validated learning with least effort',
      },
    },
    {
      id: 4,
      name: 'The Visionary Guide',
      spriteColor: '#f59e0b',
      position: { x: 4, y: 7 },
      question:
        'Which of these is a key question asked by a Product Manager rather than a Project Manager?',
      options: {
        _type: 'radio',
        _options: [
          'How do we get this done by Friday?',
          'Should we build this?',
          'How do we allocate these resources?',
          'Is the budget adherence correct?',
        ],
        _value: 'Should we build this?',
      },
    },
  ],
}

// Default crossword config
const DEFAULT_CROSSWORD_CONFIG = {
  title: 'Sample Crossword',
  grid: [],
  clues: {
    across: [],
    down: [],
  },
}

// Global listener tracking
let activeListeners = new Map()

// Improved drag listeners with safety checks
export const addDragListenersSafe = dragAreaElement => {
  // Safety check
  if (
    !dragAreaElement ||
    typeof dragAreaElement.addEventListener !== 'function'
  ) {
    return
  }

  // Remove existing listeners first
  removeDragListenersFromElement(dragAreaElement)

  const handleDragOver = e => {
    e.preventDefault()
    // Safety check for element existence
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.add('drag-over')
    }
  }

  const handleDragLeave = e => {
    e.preventDefault()
    // Safety check for element existence
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.remove('drag-over')
    }
  }

  const handleDrop = e => {
    e.preventDefault()
    // Safety check for element existence
    if (e.currentTarget && e.currentTarget.classList) {
      e.currentTarget.classList.remove('drag-over')
    }
  }

  // Add listeners
  dragAreaElement.addEventListener('dragover', handleDragOver)
  dragAreaElement.addEventListener('dragleave', handleDragLeave)
  dragAreaElement.addEventListener('drop', handleDrop)

  // Track listeners for cleanup
  activeListeners.set(dragAreaElement, {
    dragover: handleDragOver,
    dragleave: handleDragLeave,
    drop: handleDrop,
  })

  // Mark element as having listeners
  dragAreaElement.setAttribute('data-has-drag-listeners', 'true')
}

// Remove listeners from specific element
const removeDragListenersFromElement = element => {
  if (!element) return

  const listeners = activeListeners.get(element)
  if (listeners) {
    element.removeEventListener('dragover', listeners.dragover)
    element.removeEventListener('dragleave', listeners.dragleave)
    element.removeEventListener('drop', listeners.drop)
    activeListeners.delete(element)
  }

  element.removeAttribute('data-has-drag-listeners')
}

// Clean up all drag listeners - Enhanced version
const removeAllDragListeners = () => {
  try {
    // Tracked listener'ları temizle
    activeListeners.forEach((listeners, element) => {
      if (element && element.removeEventListener) {
        element.removeEventListener('dragover', listeners.dragover)
        element.removeEventListener('dragleave', listeners.dragleave)
        element.removeEventListener('drop', listeners.drop)
      }
    })
    activeListeners.clear()

    // DOM'dan listener attribute'larını kaldır
    document
      .querySelectorAll('[data-has-drag-listeners="true"]')
      .forEach(element => {
        element.removeAttribute('data-has-drag-listeners')
      })

    // Tüm drop area class'larına sahip elementleri temizle
    const allDropAreas = document.querySelectorAll(
      '.drop-area-wide, .drop-area-narrow, .drop-area-wide-wrapper',
    )
    allDropAreas.forEach(element => {
      element.classList.remove('drag-over')
      element.removeAttribute('data-has-drag-listeners')
    })
  } catch (error) {
    console.error('Error cleaning drag listeners:', error)
  }
}

// Improved timeout function
const addDragListenersWithTimeoutSafe = () => {
  // Clean up all existing listeners first
  removeAllDragListeners()

  setTimeout(() => {
    // Add listeners to narrow drop areas
    const dragAreas = document.querySelectorAll('.drop-area-narrow')
    dragAreas.forEach(area => {
      // Only add if element doesn't already have listeners
      if (area && !area.hasAttribute('data-has-drag-listeners')) {
        addDragListenersSafe(area)
      }
    })

    // Add listeners to wide drop areas
    const dropAreaWides = document.querySelectorAll('.drop-area-wide')
    dropAreaWides.forEach(area => {
      if (area && !area.hasAttribute('data-has-drag-listeners')) {
        addDragListenersSafe(area)
      }
    })
  }, 100) // Slightly longer delay to ensure DOM is ready
}

export const removeAllDropAreas = editor => {
  // Önce tüm drag listener'ları temizle
  try {
    // DOM'dan listener attribute'larını kaldır
    document
      .querySelectorAll('[data-has-drag-listeners="true"]')
      .forEach(element => {
        element.removeAttribute('data-has-drag-listeners')
      })

    // Tüm drop area class'larına sahip elementleri temizle
    const allDropAreas = document.querySelectorAll(
      '.drop-area-wide, .drop-area-narrow, .drop-area-wide-wrapper',
    )
    allDropAreas.forEach(element => {
      element.classList.remove('drag-over')
      element.removeAttribute('data-has-drag-listeners')
    })
  } catch (error) {
    console.error('Error cleaning drag listeners:', error)
  }

  // Editor kontrolü
  if (!editor || !editor.model) {
    return
  }

  try {
    // 1. Model layer'dan elementleri kaldır
    editor.model.change(writer => {
      const root = editor.model.document.getRoot()
      if (!root) {
        console.warn('No model root found')
        return
      }

      const removeDropAreasRecursive = parentElement => {
        if (!parentElement || typeof parentElement.getChildren !== 'function') {
          return
        }

        const children = Array.from(parentElement.getChildren())
        const dropAreas = children.filter(
          child =>
            child &&
            (child.name === 'dropAreaWide' ||
              child.name === 'dropAreaNarrow' ||
              child.name === 'dropAreaWideWrapper'),
        )

        dropAreas.forEach(dropArea => {
          if (dropArea) {
            try {
              writer.remove(dropArea)
            } catch (removeError) {
              console.warn('Error removing drop area from model:', removeError)
            }
          }
        })

        // Recursive traverse
        children.forEach(child => {
          if (child && child.childCount > 0) {
            removeDropAreasRecursive(child)
          }
        })
      }

      removeDropAreasRecursive(root)
    })

    // 2. View layer'dan CSS class'larını temizle
    editor.editing.view.change(writer => {
      const viewRoot = editor.editing.view.document.getRoot()

      const cleanViewRecursive = element => {
        if (!element || !element.is('element')) return

        // Drop area class'larını kaldır
        if (element.hasClass('drop-area-wide')) {
          writer.removeClass('drop-area-wide', element)
          writer.addClass('empty-dom', element)
        }
        if (element.hasClass('drop-area-narrow')) {
          writer.removeClass('drop-area-narrow', element)
          writer.addClass('empty-dom', element)
        }

        // Children'ı da temizle
        for (const child of element.getChildren()) {
          if (child.is('element')) {
            cleanViewRecursive(child)
          }
        }
      }

      for (const child of viewRoot.getChildren()) {
        cleanViewRecursive(child)
      }
    })

    // 3. DOM temizliği (emin olmak için)
    setTimeout(() => {
      const domDropAreas = document.querySelectorAll(
        '.drop-area-wide, .drop-area-narrow, .drop-area-wide-wrapper',
      )
      domDropAreas.forEach(element => {
        if (element) {
          element.classList.remove('drop-area-wide', 'drop-area-narrow')
          element.classList.add('empty-dom')
        }
      })
    }, 10)
  } catch (error) {
    console.error('removeAllDropAreas error:', error)
  }
}

// Optimized version with button visibility fix
// Combined approach: Handle top-level and nested drop areas separately
export const removeAlldropAreaWidesOptimized = (writer, editor) => {
  try {
    const root = editor.model.document.getRoot()
    if (!root) return

    // STEP 1: Handle top-level drop areas first (direct children of root)
    // This preserves the original working logic for button visibility
    const dropAreaWides = Array.from(root.getChildren()).filter(
      child => child.name === 'dropAreaWide',
    )

    dropAreaWides.forEach(dropAreaWide => {
      const index = Array.from(root.getChildren()).indexOf(dropAreaWide)
      const prevElement = index > 0 ? root.getChild(index - 1) : null
      const nextElement =
        index < root.childCount - 1 ? root.getChild(index + 1) : null

      writer.remove(dropAreaWide)

      if (prevElement && prevElement.name === 'simpleBoxWrapper') {
        updateButtonVisibility(prevElement, editor)
      }
      if (nextElement && nextElement.name === 'simpleBoxWrapper') {
        updateButtonVisibility(nextElement, editor)
      }
    })

    // STEP 2: Handle nested drop areas using walker
    // Only look for drop areas that are NOT direct children of root
    const walker = writer
      .createRangeIn(root)
      .getWalker({ ignoreElementEnd: true })
    const nestedElementsToRemove = []

    for (const value of walker) {
      if (
        value?.item?.is('element') &&
        (value.item.name === 'dropAreaWide' ||
          value.item.name === 'dropAreaNarrow' ||
          value.item.name === 'dropAreaWideWrapper') &&
        value.item.parent !== root // Only nested ones, not top-level
      ) {
        nestedElementsToRemove.push(value.item)
      }
    }

    // Remove nested elements
    nestedElementsToRemove.forEach(element => {
      try {
        writer.remove(element)
      } catch (error) {
        console.warn('Error removing nested element:', error)
      }
    })
  } catch (error) {
    console.error('removeAlldropAreaWidesOptimized error:', error)
  }
}

// Eski fonksiyon - geriye dönük uyumluluk için
export const removeAlldropAreaWides = (writer, editor) => {
  removeAllDragListeners()
  removeAlldropAreaWidesOptimized(writer, editor)
}

export const handleAddButton = editor => {
  editor.model.change(writer => {
    const selection = editor.model.document.selection
    const simpleBox = selection.getSelectedElement()
    const simpleButton = writer.createElement('simpleButton', {
      href: '',
      textcolor: '#002E47',
      text: 'Button',
      type: 'link',
      pdfname: '',
    })

    const text = writer.createText('Button')

    const simpleIcon = writer.createElement('simpleIcon', {
      icon: '',
      iconname: 'lets-icons:add-round',
    })
    writer.append(simpleIcon, simpleButton)
    writer.append(text, simpleButton)

    if (simpleBox) {
      // Function to find buttonWrapper
      const findButtonWrapper = element => {
        if (!element || !element.is('element')) return null

        if (element.name === 'buttonWrapper') {
          return element
        }

        for (const child of element.getChildren()) {
          const result = findButtonWrapper(child)
          if (result) {
            return result
          }
        }

        return null
      }

      // Find buttonWrapper in simpleBox
      const buttonWrapper = findButtonWrapper(simpleBox)

      if (buttonWrapper) {
        const flexSize = buttonWrapper.getAttribute('flex-size')

        if (flexSize === '3/1') {
          writer.append(
            simpleButton,
            editor.model.document.selection.getSelectedElement(),
          )
        } else {
          writer.append(simpleButton, buttonWrapper)
        }
      } else {
        // Fallback if no buttonWrapper found
        writer.append(simpleButton, simpleBox)
      }
    }
  })
}

export const handleAddAccordion = editor => {
  const selection = editor.model.document.selection
  const selectedElement = selection.getSelectedElement()

  if (!selectedElement) {
    console.error('No element selected')
    return
  }

  // Find the column element to insert into
  const positionElement = selectedElement.getChild(0)

  editor.model.change(writer => {
    // Create the main accordion wrapper
    const accordionWrapper = writer.createElement('accordionWrapper')
    for (const child of positionElement.getChildren()) {
      if (child.is('element', 'accordionWrapper')) {
        const item = child.getChild(0)
        if (item && item.is('element', 'accordionItem')) {
          writer.setAttribute('isOpen', false, item)
          writer.removeAttribute('addOpen', item)
        }
      }
    }
    // Create accordion item
    const accordionItem = writer.createElement('accordionItem', {
      isOpen: false, // Set initial state (maps to data-open="false")
      addOpen: true,
    })

    // Create accordion header with h3 heading
    const accordionHeader = writer.createElement('accordionHeader')
    const headerHeading = writer.createElement('heading3') // Creates <h3>
    writer.insertText('Header', headerHeading)

    // Create accordion content with paragraph content
    const accordionContent = writer.createElement('accordionContent')
    const contentParagraph = writer.createElement('paragraph')
    const simpleBoxWrapper = writer.createElement('simpleBoxWrapper')
    const simpleBox = writer.createElement('simpleBox')
    const column = writer.createElement('column')
    const textContent = `I'm a paragraph. Click to start writing to inform, inspire, or encourage your community. Happy editing!`
    writer.insertText(textContent, contentParagraph)

    // Build the structure properly
    writer.insert(headerHeading, accordionHeader)
    writer.insert(simpleBoxWrapper, accordionContent)
    writer.insert(simpleBox, simpleBoxWrapper)
    writer.insert(column, simpleBox)
    writer.insert(contentParagraph, column)

    // Insert header FIRST, then content - this ensures header is on top
    writer.insert(accordionHeader, accordionItem, 0) // Insert header at position 0 (top)
    writer.insert(accordionContent, accordionItem, 1) // Insert content at position 1 (bottom)
    writer.insert(accordionItem, accordionWrapper) // Insert item into wrapper

    // Insert the accordion wrapper into the column
    writer.insert(accordionWrapper, positionElement, 'end')

    // Force reconversion and re-render
    editor.editing.reconvertItem(selectedElement)
    editor.editing.view.forceRender()
  })
}

export const handleAddTab = editor => {
  const selection = editor.model.document.selection
  const selectedElement = selection.getSelectedElement()

  if (!selectedElement) {
    console.error('No element selected')
    return
  }

  // Find the tabContainer element
  let tabContainer = null

  // Check if selectedElement is the tabContainer itself
  if (selectedElement.name === 'tabContainer') {
    tabContainer = selectedElement
  } else {
    // Look for tabContainer in the selected element's children or ancestors
    const findTabContainer = element => {
      if (!element) return null

      if (element.name === 'tabContainer') {
        return element
      }

      // Check children (only if element has getChildren method - skip text nodes)
      if (typeof element.getChildren === 'function') {
        for (const child of element.getChildren()) {
          const found = findTabContainer(child)
          if (found) return found
        }
      }

      return null
    }

    tabContainer = findTabContainer(selectedElement)

    // If not found in children, check ancestors
    if (!tabContainer) {
      let current = selectedElement.parent
      while (current) {
        if (current.name === 'tabContainer') {
          tabContainer = current
          break
        }
        current = current.parent
      }
    }
  }

  if (!tabContainer) {
    console.error('No tabContainer found')
    return
  }

  editor.model.change(writer => {
    const tabHeaders = tabContainer.getChild(0) // tabHeaders
    const tabContents = tabContainer.getChild(1) // tabContents

    if (!tabHeaders || !tabContents) {
      console.error('TabContainer structure is invalid')
      return
    }

    const tabCount = tabHeaders.childCount + 1

    // Create new tab header with proper attributes
    const newTabHeader = writer.createElement('tabHeader', {
      tabIndex: tabCount.toString(),
      tabTitle: `Tab ${tabCount}`,
      isActive: 'false',
      icon: '', // Default empty icon
      backgroundColor: '#002e47', // Default empty background color
      textColor: '#002e47', // Default empty text color
      text: `Tab ${tabCount}`, // Default text content
    })

    // Add heading3 element to the tab header
    const headerHeading = writer.createElement('heading3')
    writer.insertText(`Tab ${tabCount}`, headerHeading)
    writer.append(headerHeading, newTabHeader)

    // Create new tab content
    const newTabContent = writer.createElement('tabContent', {
      tabIndex: tabCount.toString(),
      isActive: 'false',
    })
    const simpleBoxWrapper = writer.createElement('simpleBoxWrapper')
    const simpleBox = writer.createElement('simpleBox')
    const column = writer.createElement('column')

    // Add default content to the new tab
    const contentParagraph = writer.createElement('paragraph')
    writer.insertText(
      `I'm a paragraph. Click to start writing that will inform, inspire, or encourage your community. We know you have so many things to share with the world. Happy editing!`,
      contentParagraph,
    )
    writer.append(simpleBoxWrapper, newTabContent)
    writer.append(simpleBox, simpleBoxWrapper)
    writer.append(column, simpleBox)
    writer.append(contentParagraph, column)

    // Add to containers
    writer.append(newTabHeader, tabHeaders)
    writer.append(newTabContent, tabContents)

    // Force re-render
    editor.editing.reconvertItem(tabContainer)
    editor.editing.view.forceRender()
  })
}

export const handleAddHotspot = (editor, { simpleBox } = {}) => {
  const selection = editor.model.document.selection
  const selectedElement =
    selection.getSelectedElement() || selection.getFirstPosition()?.parent
  const searchRoot = simpleBox || selectedElement || editor.model.document.getRoot()
  let hotspotContainer = null

  // Helper function to find all carousel wrappers in model
  const findCarouselWrappers = (element, wrappers = []) => {
    if (element.name === 'imageCarouselWrapper') {
      wrappers.push(element)
    }
    if (element.getChildren) {
      for (const child of element.getChildren()) {
        findCarouselWrappers(child, wrappers)
      }
    }
    return wrappers
  }

  // First, try to find active carousel item's hotspot container from DOM.
  // Use searchRoot to stay scoped to the current simpleBox (if provided).
  const carouselWrappers = findCarouselWrappers(searchRoot)

  for (const carouselWrapper of carouselWrappers) {
    // Get DOM element for this carousel wrapper
    const viewWrapper = editor.editing.mapper.toViewElement(carouselWrapper)
    if (!viewWrapper) continue

    const domWrapper = editor.editing.view.domConverter.mapViewToDom(viewWrapper)
    if (!domWrapper) continue

    // Find active index from DOM
    const domItems = domWrapper.querySelectorAll('.image-carousel-item')
    let activeIndex = 0
    let hasActive = false

    domItems.forEach((item, i) => {
      if (item.classList.contains('active')) {
        activeIndex = i
        hasActive = true
      }
    })

    if (hasActive) {
      // Get the model items
      const modelItems = Array.from(carouselWrapper.getChildren()).filter(
        child => child.name === 'imageCarouselItem'
      )

      if (modelItems[activeIndex]) {
        // Find the imageHotspotContainer inside the active carousel item
        for (const child of modelItems[activeIndex].getChildren()) {
          if (child.name === 'imageHotspotContainer') {
            hotspotContainer = child
            break
          }
        }
      }
      if (hotspotContainer) break
    }
  }

  // Fallback: Check if selected element is the imageHotspotContainer itself
  if (!hotspotContainer && selectedElement?.name === 'imageHotspotContainer') {
    hotspotContainer = selectedElement
  }

  if (!hotspotContainer) {
    // Look for imageHotspotContainer in the search root's children or ancestors
    const findHotspotContainer = element => {
      if (!element) return null

      if (element.name === 'imageHotspotContainer') {
        return element
      }

      // Check children using childCount and getChild
      const childCount = element.childCount || 0
      for (let i = 0; i < childCount; i++) {
        const child = element.getChild(i)
        const found = findHotspotContainer(child)
        if (found) return found
      }

      return null
    }

    hotspotContainer = findHotspotContainer(searchRoot)

    // If not found in children, check ancestors
    if (!hotspotContainer) {
      let current = selectedElement?.parent
      while (current) {
        if (current.name === 'imageHotspotContainer') {
          hotspotContainer = current
          break
        }
        current = current.parent
      }
    }
  }

  editor.model.change(writer => {
    if (
      !hotspotContainer ||
      hotspotContainer.name !== 'imageHotspotContainer'
    ) {
      let element = selection.getFirstPosition().parent
      while (element && element.name !== 'imageHotspotContainer') {
        element = element.parent
      }
      hotspotContainer = element
    }

    if (hotspotContainer && hotspotContainer.name === 'imageHotspotContainer') {
      const hotspotId = 'hotspot-' + Date.now()

      // Create hotspot WITHOUT content child
      const hotspot = writer.createElement('imageHotspot', {
        x: '50',
        y: '50',
        title: 'New Hotspot',
        id: hotspotId,
        isVisible: 'false',
        backgroundColor: '#015FFB',
        iconColor: '#ffffff',
        icon: '',
        iconName: 'mdi:plus',
      })

      // Create hotspot content as SIBLING with hotspotId attribute
      const hotspotContent = writer.createElement('imageHotspotContent', {
        hotspotId: hotspotId,
      })

      // Create default structure without heading
      const simpleBoxWrapper = writer.createElement('simpleBoxWrapper')
      const simpleBox = writer.createElement('simpleBox')
      const column = writer.createElement('column', { 'flex-size': '1' })

      // Create paragraph only (no heading)
      const paragraph = writer.createElement('paragraph')
      writer.insertText(
        "I'm a paragraph. Click to start writing that will inform, inspire, or encourage your community. We know you have so many things to share with the world. Happy editing!",
        paragraph,
      )

      // Assemble structure (only paragraph, no heading)
      writer.append(paragraph, column)
      writer.append(column, simpleBox)
      writer.append(simpleBox, simpleBoxWrapper)
      writer.append(simpleBoxWrapper, hotspotContent)

      // Append both as siblings to container
      writer.append(hotspot, hotspotContainer)
      writer.append(hotspotContent, hotspotContainer)

      // Save active carousel index before reconvert
      // Find the carousel wrapper that contains this hotspotContainer
      let carouselWrapperModel = hotspotContainer.parent
      while (carouselWrapperModel && carouselWrapperModel.name !== 'imageCarouselWrapper') {
        carouselWrapperModel = carouselWrapperModel.parent
      }

      let savedActiveIndex = 0
      if (carouselWrapperModel) {
        const viewWrapper = editor.editing.mapper.toViewElement(carouselWrapperModel)
        if (viewWrapper) {
          const domWrapper = editor.editing.view.domConverter.mapViewToDom(viewWrapper)
          if (domWrapper) {
            const allItems = domWrapper.querySelectorAll('.image-carousel-item')
            allItems.forEach((item, i) => {
              if (item.classList.contains('active')) {
                savedActiveIndex = i
              }
            })
          }
        }
        // Set this so initCarousel uses it
        editor._carouselActiveIndex = savedActiveIndex
      }

      // Force re-render
      editor.editing.reconvertItem(hotspotContainer)
      editor.editing.view.forceRender()
    }
  })
}
// Helper function to find a node by name/type
const findNodeByName = (node, targetName, maxDepth = 10) => {
  if (!node || maxDepth === 0) return null

  // Check if current node matches
  if (node.name === targetName || node.is?.(targetName)) {
    return node
  }

  // Search in children
  const childCount = node.childCount || 0
  for (let i = 0; i < childCount; i++) {
    const child = node.getChild(i)
    const found = findNodeByName(child, targetName, maxDepth - 1)
    if (found) return found
  }

  return null
}

// Helper function to find a node by attribute
const findNodeByAttribute = (
  node,
  attributeName,
  attributeValue,
  maxDepth = 10,
) => {
  if (!node || maxDepth === 0) return null

  // Check if current node has the attribute
  if (node.getAttribute?.(attributeName) === attributeValue) {
    return node
  }

  // Search in children
  const childCount = node.childCount || 0
  for (let i = 0; i < childCount; i++) {
    const child = node.getChild(i)
    const found = findNodeByAttribute(
      child,
      attributeName,
      attributeValue,
      maxDepth - 1,
    )
    if (found) return found
  }

  return null
}

export const handleAddNumberedCard = editor => {
  const selection = editor.model.document.selection
  const selectedElement = selection.getSelectedElement()

  if (!selectedElement) {
    console.error('No element selected')
    return
  }

  const firstChild = selectedElement.getChild(0)
  if (!firstChild) {
    console.error('Selected element has no children')
    return
  }

  // Find position element (kartların tutulduğu container)
  // Genellikle 'cardWrapper' veya 'cardsContainer' gibi bir isim olabilir
  const positionElement =
    findNodeByName(firstChild, 'cardsContainer') ||
    findNodeByName(firstChild, 'cardWrapper') ||
    firstChild.getChild(0) // Fallback

  // Find card type element
  const typeCard =
    findNodeByAttribute(firstChild, 'type', 'numberedCard') ||
    findNodeByAttribute(firstChild, 'type', 'card') ||
    findNodeByAttribute(firstChild, 'type', 'flipCard') ||
    findNodeByAttribute(firstChild, 'type', 'highlightCard')

  if (!typeCard) {
    console.error('Could not find card type element')
    return
  }

  const cardType = typeCard.getAttribute('type')

  if (cardType === 'numberedCard') {
    editor.model.change(writer => {
      // Create the main card element
      const cardBox = writer.createElement('cardBox', {
        type: 'numberedCard',
      })

      // Create number container
      const cardHeader = writer.createElement('cardHeader')
      const pNumber = writer.createElement('paragraph')
      const number = '0' + (positionElement?._children?._nodes?.length + 1)
      const textNumber = writer.createText(number.toString())

      // Create content container
      const cardContent = writer.createElement('cardContent')
      const pContent = writer.createElement('paragraph')
      const textContent = `I'm a paragraph. Click to start writing to inform, inspire, or encourage your community. Happy editing!`

      // Build the structure safely
      writer.append(cardHeader, cardBox)
      writer.append(pNumber, cardHeader)
      writer.append(textNumber, pNumber)

      writer.append(cardContent, cardBox)
      writer.append(pContent, cardContent)
      writer.append(textContent, pContent)

      // Insert at the correct position
      if (positionElement) {
        writer.append(cardBox, positionElement)
      }
    })
  } else if (cardType === 'card') {
    editor.model.change(writer => {
      // Create the main card element
      const cardBox = writer.createElement('cardBox', {
        type: 'card',
      })

      // Create header with title
      const cardHeader = writer.createElement('cardHeader')
      const pTitle = writer.createElement('paragraph')
      const textTitle = writer.createText('Header')

      // Create content container
      const cardContent = writer.createElement('cardContent')
      const pContent = writer.createElement('paragraph')
      const textContent = `I'm a paragraph. Click to start writing to inform, inspire, or encourage your community. Happy editing!`

      // Build the structure safely
      writer.append(cardHeader, cardBox)
      writer.append(pTitle, cardHeader)
      writer.append(textTitle, pTitle)

      writer.append(cardContent, cardBox)
      writer.append(pContent, cardContent)
      writer.append(textContent, pContent)

      // Insert at the correct position
      if (positionElement) {
        writer.append(cardBox, positionElement)
      }
    })
  } else if (cardType === 'flipCard') {
    editor.model.change(writer => {
      // Create the main card element
      const cardBox = writer.createElement('cardBox', {
        type: 'flipCard',
      })

      // Create front and back cards
      const flipCardFront = writer.createElement('flipCardFront')
      const flipCardBack = writer.createElement('flipCardBack')
      const pContentFront = writer.createElement('paragraph')
      const pContentBack = writer.createElement('paragraph')

      const cardNumber = positionElement?._children?._nodes?.length + 1
      const flipCardFrontText = `Front of Card ${cardNumber}`
      const flipCardBackText = `Back of Card ${cardNumber}`

      // Build the structure safely
      writer.append(flipCardFront, cardBox)
      writer.append(flipCardBack, cardBox)
      writer.append(pContentFront, flipCardFront)
      writer.append(pContentBack, flipCardBack)

      writer.append(flipCardFrontText, pContentFront)
      writer.append(flipCardBackText, pContentBack)

      // Insert at the correct position
      if (positionElement) {
        const position = writer.createPositionAt(positionElement, 'end')
        writer.insert(cardBox, position)
        editor.editing.reconvertItem(positionElement)
        editor.editing.view.forceRender()
      } else {
        const position = selection.getFirstPosition()
        writer.insert(cardBox, position)
      }
    })
  } else {
    // highlightCard
    editor.model.change(writer => {
      // Create the main card element
      const cardBox = writer.createElement('cardBox', {
        type: 'highlightCard',
      })

      // Create header with icon
      const cardHeader = writer.createElement('cardHeader')
      const cardIconButton = writer.createElement('cardIconButton')
      const simpleIcon = writer.createElement('simpleIcon', {
        icon: 'lets-icons:add-round',
        iconname: 'lets-icons:add-round',
      })

      // Create content container
      const cardContent = writer.createElement('cardContent')
      const pContent = writer.createElement('paragraph')
      const textContent = `Normal text`

      // Build the structure safely
      writer.append(cardHeader, cardBox)
      writer.append(cardIconButton, cardHeader)
      writer.append(simpleIcon, cardIconButton)

      writer.append(cardContent, cardBox)
      writer.append(pContent, cardContent)
      writer.append(textContent, pContent)

      // Insert at the correct position
      if (positionElement) {
        writer.append(cardBox, positionElement)
      }
    })
  }
}

// Updated removeDropAreaNarrow with proper cleanup
export const removeDropAreaNarrow = editor => {
  // First clean up all drag listeners
  removeAllDragListeners()

  // Editor kontrolü
  if (!editor || !editor.model) {
    return
  }

  try {
    editor.model.change(writer => {
      const root = editor.model.document.getRoot()
      if (!root) {
        console.warn('No root element found')
        return
      }

      const removeAllDropAreas = parentElement => {
        if (!parentElement || typeof parentElement.getChildren !== 'function') {
          return
        }

        try {
          const children = Array.from(parentElement.getChildren())
          const dropAreas = children.filter(
            child => child && child.name === 'dropAreaNarrow',
          )

          dropAreas.forEach(dropArea => {
            if (dropArea) {
              try {
                writer.remove(dropArea)
              } catch (removeError) {
                console.warn('Error removing drop area:', removeError)
              }
            }
          })

          children.forEach(child => {
            if (child && child.childCount > 0) {
              removeAllDropAreas(child)
            }
          })
        } catch (childrenError) {
          console.warn('Error processing children:', childrenError)
        }
      }

      removeAllDropAreas(root)
    })
  } catch (error) {
    console.error('removeDropAreaNarrow error:', error)
  }
}

// Optimized version with safety checks
export const removeDropAreaNarrowOptimized = editor => {
  // First clean up all drag listeners
  removeAllDragListeners()

  try {
    editor.model.change(writer => {
      const root = editor.model.document.getRoot()
      if (!root) {
        console.warn('No root element found for optimization')
        return
      }

      let walker,
        elementsToRemove = []

      try {
        walker = writer
          .createRangeIn(root)
          .getWalker({ ignoreElementEnd: true })
      } catch (walkerError) {
        console.warn('Walker creation error, falling back to recursive method')
        // Fallback to recursive method
        removeDropAreaNarrow(editor)
        return
      }

      // Safely collect all dropAreaNarrow elements
      try {
        for (const value of walker) {
          // Multi-level safety check
          if (
            value &&
            value.item &&
            typeof value.item.is === 'function' &&
            value.item.is('element') &&
            value.item.name === 'dropAreaNarrow'
          ) {
            elementsToRemove.push(value.item)
          }
        }
      } catch (walkerIterationError) {
        console.warn('Walker iteration error:', walkerIterationError)
        // Try alternative approach
        try {
          // Fallback: manually traverse the tree
          const traverseAndCollect = element => {
            if (!element || typeof element.getChildren !== 'function') return

            for (const child of element.getChildren()) {
              if (child && child.name === 'dropAreaNarrow') {
                elementsToRemove.push(child)
              }
              if (child && child.childCount > 0) {
                traverseAndCollect(child)
              }
            }
          }
          traverseAndCollect(root)
        } catch (fallbackError) {
          console.error('Fallback collection also failed:', fallbackError)
          return
        }
      }

      // Safely remove all collected elements
      elementsToRemove.forEach((element, index) => {
        if (element) {
          try {
            writer.remove(element)
          } catch (removeError) {
            console.warn(
              `Error removing element at index ${index}:`,
              removeError,
            )
          }
        }
      })
    })
  } catch (error) {
    console.error('removeDropAreaNarrowOptimized error:', error)
  }
}

export const createDropFromHtml = (writer, html, editor) => {
  const viewFragment = editor.data.processor.toView(html)
  const modelFragment = editor.data.toModel(viewFragment)
  const dropAreaWide = writer.createElement('dropAreaWide')
  writer.append(modelFragment, dropAreaWide)
  return dropAreaWide
}

export const adddropAreaWidesAroundAll = (writer, position = null, editor) => {
  // Clean up first
  removeDropAreaNarrow(editor)

  const root = window.editor.model.document.getRoot()
  const simpleBoxWrappers = Array.from(root.getChildren()).filter(
    child => child && child.name === 'simpleBoxWrapper',
  )

  // Helper function to check if element is any type of drop area
  const isdropAreaWide = element => {
    return (
      element &&
      (element.name === 'dropAreaWide' ||
        element.name === 'dropAreaWideWrapper' ||
        element.name === 'dropAreaNarrow')
    )
  }

  // Recursive function to traverse all elements and find simpleBoxWrappers at any depth
  const addDropAreasForWrapper = parentElement => {
    const allChildren = Array.from(parentElement.getChildren())
    const childWrappers = []

    // Find all simpleBoxWrapper children at this level
    allChildren.forEach(child => {
      if (child && child.name === 'simpleBoxWrapper') {
        childWrappers.push(child)
      }
    })

    // Add drop areas for wrappers found at this level
    childWrappers.forEach((wrapper, index) => {
      const wrapperIndex = allChildren.indexOf(wrapper)
      const previousElement =
        wrapperIndex > 0 ? allChildren[wrapperIndex - 1] : null
      const nextElement =
        wrapperIndex < allChildren.length - 1
          ? allChildren[wrapperIndex + 1]
          : null

      // Add drop area before wrapper if needed
      if (!isdropAreaWide(previousElement)) {
        window.editor.model.change(writer => {
          const dropAreaWide = writer.createElement('dropAreaNarrow')
          writer.insert(dropAreaWide, wrapper, 'before')
        })
      }

      // Add drop area after the last wrapper if needed
      if (index === childWrappers.length - 1 && !isdropAreaWide(nextElement)) {
        window.editor.model.change(writer => {
          const lastdropAreaWide = writer.createElement('dropAreaNarrow')
          writer.insert(lastdropAreaWide, wrapper, 'after')
        })
      }
    })

    // Recursively traverse ALL child elements (not just simpleBoxWrapper)
    allChildren.forEach(child => {
      if (child && child.childCount > 0) {
        addDropAreasForWrapper(child)
      }
    })
  }

  // Add drop areas for top-level wrappers
  simpleBoxWrappers.forEach((wrapper, index) => {
    if (!wrapper) return

    const wrapperIndex = Array.from(root.getChildren()).indexOf(wrapper)
    const previousElement =
      wrapperIndex > 0 ? root.getChild(wrapperIndex - 1) : null
    const nextElement =
      wrapperIndex < root.childCount - 1
        ? root.getChild(wrapperIndex + 1)
        : null

    if (!isdropAreaWide(previousElement)) {
      window.editor.model.change(writer => {
        const dropAreaWide = writer.createElement('dropAreaNarrow')
        writer.insert(dropAreaWide, wrapper, 'before')
      })
    }

    if (
      index === simpleBoxWrappers.length - 1 &&
      !isdropAreaWide(nextElement)
    ) {
      window.editor.model.change(writer => {
        const lastdropAreaWide = writer.createElement('dropAreaNarrow')
        writer.insert(lastdropAreaWide, wrapper, 'after')
      })
    }

    // Add drop areas for nested wrappers inside this wrapper
    addDropAreasForWrapper(wrapper)
  })

  if (simpleBoxWrappers.length === 0 && position) {
    handleNoWrappersCase(position, root, isdropAreaWide)
  }

  // Add listeners with safe method
  addDragListenersWithTimeoutSafe()
}

const hideAllMessageBoxes = dispatch => {
  dispatch(messageBoxHideAction())
}

export const handleButtonOptions = (dispatch, editor, target) => {
  hideAllMessageBoxes(dispatch)
  const selectedElement = editor.model.document.selection.getSelectedElement()

  // Add click outside listener
  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-0')
    const messageBoxTwo = document.getElementById('message-box-1')
    const chromePicker = document.getElementsByClassName('chrome-picker')

    if (chromePicker.length !== 0) {
      if (
        messageBox &&
        !messageBox.contains(event.target) &&
        messageBoxTwo &&
        !messageBoxTwo.contains(event.target) &&
        chromePicker &&
        !chromePicker.contains(event.target)
      ) {
        document.removeEventListener('mousedown', handleClickOutside)
        dispatch(messageBoxHideAction())
      }
    } else {
      if (
        messageBox &&
        !messageBox.contains(event.target) &&
        messageBoxTwo &&
        !messageBoxTwo.contains(event.target)
      ) {
        document.removeEventListener('mousedown', handleClickOutside)
        dispatch(messageBoxHideAction())
      } else if (
        messageBox &&
        !messageBox.contains(event.target) &&
        !messageBoxTwo
      ) {
        dispatch(messageBoxHideAction())
      }
    }
  }

  document.addEventListener('mousedown', handleClickOutside)

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
            <Typography>Button Options</Typography>
          </div>`,
        buttons: [],
        component: (
          <ButtonOptionsModal
            editor={editor}
            target={target}
            selection={selectedElement}
            position={editor.model.document.selection.getFirstPosition()}
          />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => {
          document.removeEventListener('mousedown', handleClickOutside)
        },
      }),
    ),
  )
}

export const handleTabOptions = (
  dispatch,
  editor,
  target,
  modelElementFromClick,
  position,
  selectedElement,
) => {
  hideAllMessageBoxes(dispatch)

  // Add click outside listener
  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-0')
    const messageBoxTwo = document.getElementById('message-box-1')
    const chromePicker = document.getElementsByClassName('chrome-picker')

    if (chromePicker.length !== 0) {
      if (
        messageBox &&
        !messageBox.contains(event.target) &&
        messageBoxTwo &&
        !messageBoxTwo.contains(event.target) &&
        chromePicker &&
        !chromePicker.contains(event.target)
      ) {
        document.removeEventListener('mousedown', handleClickOutside)
        dispatch(messageBoxHideAction())
      }
    } else {
      if (
        messageBox &&
        !messageBox.contains(event.target) &&
        messageBoxTwo &&
        !messageBoxTwo.contains(event.target)
      ) {
        document.removeEventListener('mousedown', handleClickOutside)
        dispatch(messageBoxHideAction())
      } else if (
        messageBox &&
        !messageBox.contains(event.target) &&
        !messageBoxTwo
      ) {
        dispatch(messageBoxHideAction())
      }
    }
  }

  document.addEventListener('mousedown', handleClickOutside)

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
            <Typography>Tab Options</Typography>
          </div>`,
        buttons: [],
        component: (
          <TabOptionsModals
            editor={editor}
            target={target}
            selection={selectedElement}
            position={position}
          />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => {
          document.removeEventListener('mousedown', handleClickOutside)
        },
      }),
    ),
  )
}

export const handleHeaderSettingsModal = (dispatch, pageUrl) => {
  // Track the latest settings between renders
  let latestThemeSettings = null

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 16px; font-weight: 400; line-height: 150%;">
            <Typography>Header settings</Typography>
          </div>`,
        buttons: [
          new ButtonData(
            ButtonType.NORMAL,
            'Cancel',
            ButtonActionType.NO,
            () => {
              dispatch(messageBoxHideAction())
            },
          ),
          new ButtonData(
            ButtonType.ACTION,
            'Save',
            ButtonActionType.YES,
            () => {
              if (latestThemeSettings) {
                const patchAction =
                  pageUrl === 'mini-course'
                    ? patchCourseLandingPageAction
                    : patchCollectionLandingPageAction

                // Build the sections patch
                // Use the sections directly from latestThemeSettings since they already contain preserved values

                const sectionsPath = {
                  path: 'sections',
                  value: latestThemeSettings.landingPageSettings?.sections || [
                    {
                      type: editorEnum.headerType,
                      content: {
                        alignment:
                          latestThemeSettings.selectedPosition || 'center',
                        image: latestThemeSettings.background || '',
                        type: latestThemeSettings.selectedType || 1,
                        description: latestThemeSettings.description || '',
                        backgroundColor:
                          latestThemeSettings.colorProperties
                            ?.backgroundColor || '#FFFFFF',
                        textColor:
                          latestThemeSettings.colorProperties?.textColor ||
                          '#002E47',
                        priceTagActive:
                          latestThemeSettings.priceTagActive || false,
                        priceValue: latestThemeSettings.priceValue || '',
                        selectedCurrency: latestThemeSettings.selectedCurrency || {
                          symbol: '$',
                          code: 'USD',
                          name: '$ (USD)',
                        },
                        thumbnailImage:
                          latestThemeSettings.thumbnailImage ||
                          'https://cdn.minicoursegenerator.com/assets/editor/header-card-thumbnail.jpg',
                        features: latestThemeSettings.features || [
                          { id: 1, text: 'Feature one', checked: true, icon: 'material-symbols:check-rounded' },
                          { id: 2, text: 'Feature two', checked: true, icon: 'material-symbols:check-rounded' },
                          { id: 3, text: 'Feature three', checked: true, icon: 'material-symbols:check-rounded' },
                          { id: 4, text: 'Feature four', checked: true, icon: 'material-symbols:check-rounded' },
                        ],
                      },
                    },
                    {
                      type: editorEnum.mainType,
                      content: {
                        html: '',
                      },
                    },
                  ],
                }

                // Build the patches array
                const patches = [sectionsPath]

                // Only add localizations patch if it exists in the latest settings
                // (meaning there were changes)
                if (
                  latestThemeSettings.startButton ||
                  latestThemeSettings.resumeButton ||
                  latestThemeSettings.readMore
                ) {
                  const localizationsValue = {}

                  if (latestThemeSettings.startButton) {
                    localizationsValue.startButton =
                      latestThemeSettings.startButton
                  }
                  if (latestThemeSettings.resumeButton) {
                    localizationsValue.resumeButton =
                      latestThemeSettings.resumeButton
                  }
                  if (latestThemeSettings.readMore) {
                    localizationsValue.readMore = latestThemeSettings.readMore
                  }

                  patches.push({
                    path: 'localizations',
                    value: localizationsValue,
                  })
                }

                dispatch(patchAction(latestThemeSettings.id, patches))

                // Update DOM elements for position
                const positionMap = {
                  left: 'flex-start',
                  center: 'center',
                  right: 'flex-end',
                }
                Array.from(
                  document.getElementsByClassName('editor-header-content'),
                ).forEach(element => {
                  element.style.alignItems =
                    positionMap[latestThemeSettings.selectedPosition]
                  element.style.textAlign = latestThemeSettings.selectedPosition
                })
                // Update when save happens
                // Update DOM elements for background
                Array.from(
                  document.getElementsByClassName('editor-header-container'),
                ).forEach(element => {
                  if (latestThemeSettings.selectedType !== 2) {
                    element.style.background = headerBackgroundCss(
                      latestThemeSettings.background,
                      0.3,
                      latestThemeSettings.landingPageSettings?.primaryColor,
                    )
                  } else {
                    const landingPageSection = latestThemeSettings?.landingPageSettings?.sections.find(
                      x => x.type === editorEnum.headerType,
                    )
                    const backgroundColor =
                      landingPageSection?.content?.backgroundColor || '#FFFFFF'
                    element.style.backgroundColor = backgroundColor
                  }
                })

                // Update button texts only if localizations exist (meaning they changed)
                if (
                  latestThemeSettings.startButton ||
                  latestThemeSettings.resumeButton ||
                  latestThemeSettings.readMore
                ) {
                  const startButtonText = latestThemeSettings.startButton
                  if (startButtonText) {
                    // Update all start button variations across all templates
                    document
                      .querySelectorAll(
                        '.start-button-text-header, .start-button-text, .hero-start-course-btn, .promo-get-started-btn',
                      )
                      .forEach(
                        element => (element.textContent = startButtonText),
                      )
                  }

                  const resumeButtonText = latestThemeSettings.resumeButton
                  if (resumeButtonText) {
                    document
                      .querySelectorAll(
                        '.resume-button-text-header, .resume-button-text',
                      )
                      .forEach(
                        element => (element.textContent = resumeButtonText),
                      )
                  }

                  // Update read more button text for collections
                  const readMoreText = latestThemeSettings.readMore
                  if (readMoreText) {
                    document
                      .querySelectorAll(
                        '.hero-see-more-btn, .promo-read-more-btn',
                      )
                      .forEach(element => (element.textContent = readMoreText))
                  }
                }

                // Update card settings (thumbnail image and features) immediately after save
                if (latestThemeSettings.thumbnailImage) {
                  const thumbnailImageUrl = latestThemeSettings.thumbnailImage.startsWith(
                    'http',
                  )
                    ? latestThemeSettings.thumbnailImage
                    : latestThemeSettings.thumbnailImage.startsWith('/')
                      ? `https://cdn.minicoursegenerator.com${latestThemeSettings.thumbnailImage}`
                      : `https://cdn.minicoursegenerator.com/${latestThemeSettings.thumbnailImage}`

                  document
                    .querySelectorAll('.promo-thumbnail-image')
                    .forEach(element => {
                      element.src = thumbnailImageUrl
                    })
                }

                // Update logo immediately after save
                const logoFromSettings =
                  latestThemeSettings.landingPageSettings?.logo
                if (logoFromSettings) {
                  const timestamp = new Date().getTime()
                  const logoUrl = logoFromSettings.startsWith('https')
                    ? `${logoFromSettings}?t=${timestamp}`
                    : `https://cdn.minicoursegenerator.com/${logoFromSettings}?t=${timestamp}`

                  // Update logo in all header templates (default, hero, promo)
                  document
                    .querySelectorAll('.header-logo, .hero-logo, .promo-logo')
                    .forEach(element => {
                      element.src = logoUrl
                    })
                }

                // Update features list
                if (latestThemeSettings.features) {
                  const featurePrimaryColor = latestThemeSettings.landingPageSettings?.primaryColor || '#015FFB'
                  const featureDefaultIcon = 'material-symbols:check-rounded'
                  const featureEncodedColor = encodeURIComponent(featurePrimaryColor)
                  const featuresHtml = latestThemeSettings.features
                    .filter(feature => feature.checked)
                    .map(feature => {
                      const featureIcon = feature.icon || featureDefaultIcon
                      const iconPath = featureIcon.replace(':', '/')
                      const iconUrl = `https://api.iconify.design/${iconPath}.svg?color=${featureEncodedColor}`

                      return `
                    <div class="promo-feature-item">
                      <img src="${iconUrl}" class="promo-check-icon" alt="" width="24" height="24" />
                      <span class="promo-feature-text" style="font-family: ${latestThemeSettings.font || 'Inter'
                        } !important; color: #002E47 !important">${feature.text}</span>
                    </div>
                  `
                    })
                    .join('')

                  document
                    .querySelectorAll('.promo-features-list')
                    .forEach(element => {
                      element.innerHTML = featuresHtml
                    })
                }

                // Update hero template colors (backgroundColor and textColor)
                if (latestThemeSettings.colorProperties) {
                  const backgroundColor =
                    latestThemeSettings.colorProperties.backgroundColor ||
                    '#FFFFFF'
                  const textColor =
                    latestThemeSettings.colorProperties.textColor

                  // Update hero background color
                  if (backgroundColor) {
                    document
                      .querySelectorAll('.editor-header-container')
                      .forEach(element => {
                        element.style.backgroundColor = backgroundColor
                      })
                  }

                  // Update hero text color
                  if (textColor) {
                    document
                      .querySelectorAll('.hero-title, .hero-description')
                      .forEach(element => {
                        element.style.color = textColor
                      })
                  }
                }
              }

              dispatch(messageBoxHideAction())
            },
          ),
        ],
        maxWidth: '920px',
        component: (
          <HeaderSettings
            onSave={settings => {
              // Store the complete settings object
              latestThemeSettings = settings
            }}
          />
        ),
        callback: () => {
          dispatch(messageBoxHideAction())
        },
      }),
    ),
  )
}

export const handleAddModal = (dispatch, editor, selection) => {
  hideAllMessageBoxes(dispatch)

  let isSelectOpen = false

  // Function to check if clicked element is part of a select component
  const isSelectComponent = element => {
    // Check for various MUI select-related classes
    return (
      element.classList?.contains('MuiSelect-select') ||
      element.classList?.contains('MuiMenu-paper') ||
      element.classList?.contains('MuiMenuItem-root') ||
      element.classList?.contains('MuiBackdrop-root') ||
      element.getAttribute('role') === 'listbox' ||
      element.getAttribute('role') === 'option' ||
      // Also check parents
      element.closest('.MuiSelect-root') ||
      element.closest('.MuiMenu-paper') ||
      element.closest('[role="listbox"]')
    )
  }

  // Add event listener for select open state
  const handleSelectOpen = () => {
    isSelectOpen = true
  }

  const handleSelectClose = () => {
    isSelectOpen = false
  }

  // Find and add listeners to select elements
  setTimeout(() => {
    const selectElements = document.querySelectorAll(
      '.MuiSelect-select, [role="button"][aria-haspopup="listbox"]',
    )
    selectElements.forEach(select => {
      select.addEventListener('mousedown', handleSelectOpen)
      // We need to monitor document for the close event
      document.addEventListener('mouseup', e => {
        // Small delay to let the click event fully process
        setTimeout(() => {
          if (!isSelectComponent(e.target)) {
            handleSelectClose()
          }
        }, 100)
      })
    })
  }, 100) // Small delay to ensure modal is rendered

  // Setup the outside click handler
  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-0')
    const messageBoxTwo = document.getElementById('message-box-1')
    const chromePicker = document.getElementsByClassName('chrome-picker')[0]

    // If select is open, don't close the modal
    if (isSelectOpen || isSelectComponent(event.target)) {
      return
    }

    // Check if chrome picker is open
    if (
      chromePicker &&
      (chromePicker === event.target || chromePicker.contains(event.target))
    ) {
      return
    }

    // Check message boxes
    if (messageBox && messageBox.contains(event.target)) {
      return
    }

    if (messageBoxTwo && messageBoxTwo.contains(event.target)) {
      return
    }

    // If we get here, user clicked outside all relevant elements
    document.removeEventListener('mousedown', handleClickOutside)
    dispatch(messageBoxHideAction())
  }

  document.addEventListener('mousedown', handleClickOutside)

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
            <Typography>Block Options</Typography>
          </div>`,
        buttons: [],
        component: <BlockOptionsModal editor={editor} selection={selection} />,
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => {
          // Clean up event listeners when modal is closed
          document.removeEventListener('mousedown', handleClickOutside)
          const selectElements = document.querySelectorAll(
            '.MuiSelect-select, [role="button"][aria-haspopup="listbox"]',
          )
          selectElements.forEach(select => {
            select.removeEventListener('mousedown', handleSelectOpen)
          })
        },
      }),
    ),
  )
}

// editor-utils.js dosyasına eklenecek fonksiyon
export const handleHotspotSettings = (
  dispatch,
  editor,
  target,
  modelElement,
  position,
) => {
  hideAllMessageBoxes(dispatch)

  // Add click outside listener
  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-0')
    const messageBoxTwo = document.getElementById('message-box-1')
    const chromePicker = document.getElementsByClassName('chrome-picker')

    if (chromePicker.length !== 0) {
      if (
        messageBox &&
        !messageBox.contains(event.target) &&
        messageBoxTwo &&
        !messageBoxTwo.contains(event.target) &&
        chromePicker &&
        !chromePicker.contains(event.target)
      ) {
        document.removeEventListener('mousedown', handleClickOutside)
        dispatch(messageBoxHideAction())
      }
    } else {
      if (
        messageBox &&
        !messageBox.contains(event.target) &&
        messageBoxTwo &&
        !messageBoxTwo.contains(event.target)
      ) {
        document.removeEventListener('mousedown', handleClickOutside)
        dispatch(messageBoxHideAction())
      } else if (
        messageBox &&
        !messageBox.contains(event.target) &&
        !messageBoxTwo
      ) {
        dispatch(messageBoxHideAction())
      }
    }
  }

  document.addEventListener('mousedown', handleClickOutside)

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
            <Typography>Hotspot Settings</Typography>
          </div>`,
        buttons: [],
        component: (
          <HotspotSettingsModal
            editor={editor}
            target={target}
            selection={modelElement}
            position={position}
          />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => {
          document.removeEventListener('mousedown', handleClickOutside)
        },
      }),
    ),
  )
}

export const handleIconButtonOptions = (dispatch, editor, target) => {
  hideAllMessageBoxes(dispatch)
  const position = editor.model.document.selection.getFirstPosition()

  // Add click outside listener
  const handleIconSelect = icon => {
    if (editor) {
      editor.model.change(writer => {
        const cardIconButton = writer.createElement('cardIconButton')

        const simpleIcon = writer.createElement('simpleIcon', {
          icon: icon,
          iconname: icon,
          width: 24,
          height: 24,
        })

        // Try to get the existing button at position
        const existingButton = position.parent.getChild(position.offset)
        if (existingButton && existingButton.name === 'cardIconButton') {
          writer.remove(existingButton)
        }

        editor.model.insertContent(cardIconButton, position)
        writer.append(simpleIcon, cardIconButton)
      })
    }
  }

  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-0')
    if (messageBox && !messageBox.contains(event.target)) {
      document.removeEventListener('mousedown', handleClickOutside)
      dispatch(messageBoxHideAction('message-box-0'))
    }
  }

  document.addEventListener('mousedown', handleClickOutside)
  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
            <Typography>Select Icon</Typography>
          </div>`,
        buttons: [],
        component: (
          <SelectedIcon
            editor={editor}
            target={target}
            onIconSelect={handleIconSelect}
          />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => { },
      }),
    ),
  )
}

const handleNoWrappersCase = (position, root, isdropAreaWide) => {
  const positionIndex = Array.from(root.getChildren()).indexOf(position)
  const previousElement =
    positionIndex > 0 ? root.getChild(positionIndex - 1) : null
  const nextElement =
    positionIndex < root.childCount - 1
      ? root.getChild(positionIndex + 1)
      : null

  if (!isdropAreaWide(previousElement) && !isdropAreaWide(nextElement)) {
    window.editor.model.change(writer => {
      const dropAreaWide = writer.createElement('dropAreaNarrow')
      writer.insert(dropAreaWide, position)
    })

    setTimeout(() => {
      const dragArea = document.querySelector('.drop-area-narrow')
      if (dragArea && !dragArea.hasAttribute('data-has-drag-listeners')) {
        addDragListenersSafe(dragArea)
      }
    }, 100)
  }
}

export const updateButtonVisibility = (containerElement, editor) => {
  try {
    // Short delay to ensure the DOM is updated
    setTimeout(() => {
      // Find all wrapper elements in the DOM
      const wrapperElements = document.querySelectorAll(
        '.simple-box-wrapper, .dynamic-list',
      )

      // Exit if no elements found
      if (!wrapperElements.length) {
        return
      }

      // Get all drop areas
      const dropAreas = document.querySelectorAll('.drop-area-wide')

      // Process each wrapper element
      wrapperElements.forEach(wrapper => {
        // Find the add buttons for this wrapper
        const addAboveButton = wrapper.querySelector(
          '.simple-box-wrapper-add-above, .dynamic-list-add-above',
        )
        const addBelowButton = wrapper.querySelector(
          '.simple-box-wrapper-add-below',
        )

        // Skip if no buttons found
        if (!addAboveButton && !addBelowButton) {
          return
        }

        // Determine if there's a drop area above/below this wrapper
        const wrapperRect = wrapper.getBoundingClientRect()

        let hasDropAbove = false
        let hasDropBelow = false

        // Check each drop area to see if it's above or below this wrapper
        dropAreas.forEach(dropArea => {
          const dropRect = dropArea.getBoundingClientRect()

          // If drop area is directly above this wrapper
          if (Math.abs(dropRect.bottom - wrapperRect.top) < 2) {
            hasDropAbove = true
          }

          // If drop area is directly below this wrapper
          if (Math.abs(dropRect.top - wrapperRect.bottom) < 2) {
            hasDropBelow = true
          }
        })

        // Update button visibility based on drop area presence
        if (addAboveButton) {
          addAboveButton.style.display = hasDropAbove ? 'none' : 'block'
        }

        if (addBelowButton) {
          addBelowButton.style.display = hasDropBelow ? 'none' : 'block'
        }
      })
    }, 50) // Small delay to ensure DOM is updated
  } catch (error) {
    console.error('Error in updateButtonVisibility:', error)
  }
}

const updateButtonStyles = (
  writer,
  addAboveButton,
  addBelowButton,
  initialdropAreaWidesLength,
  dropAreaWides,
  hasDropAbove,
  hasDropBelow,
) => {
  if (addAboveButton) {
    writer.setAttribute(
      'style',
      hasDropAbove ? 'display: none' : 'display: block',
      addAboveButton,
    )
  }
  if (initialdropAreaWidesLength === 0) {
    if (addAboveButton) {
      writer.setAttribute('style', 'display: block', addAboveButton)
    }
    if (addBelowButton) {
      writer.setAttribute('style', 'display: block', addBelowButton)
    }
  } else {
    if (addBelowButton) {
      writer.setAttribute(
        'style',
        dropAreaWides.length === 0
          ? 'display: block'
          : hasDropBelow
            ? 'display: none'
            : 'display: block',
        addBelowButton,
      )
    }
  }
}

/**
 * Recursively searches the CKEditor View tree starting from a given element
 * to find all descendant elements that have the 'drop-area-wide' class.
 *
 * @param {module:engine/view/element~Element} viewElement The starting view element (e.g., the DocumentRoot).
 * @returns {Array<module:engine/view/element~Element>} An array of matching elements.
 */
const findDropAreaWides = viewElement => {
  let dropAreaWides = []

  // Check the current element itself
  if (viewElement.hasClass && viewElement.hasClass('drop-area-wide')) {
    dropAreaWides.push(viewElement)
  }

  // Recurse through children if it's a container element (like DocumentRoot, div, p, etc.)
  if (
    viewElement.is('containerElement') ||
    viewElement.is('documentFragment')
  ) {
    for (const child of viewElement.getChildren()) {
      dropAreaWides = dropAreaWides.concat(findDropAreaWides(child))
    }
  }

  return dropAreaWides
}

/**
 * Recursively searches the CKEditor View tree starting from a given element
 * to find all descendant elements that have the 'drop-area-narrow' class.
 *
 * @param {module:engine/view/element~Element} viewElement The starting view element (e.g., the DocumentRoot).
 * @returns {Array<module:engine/view/element~Element>} An array of matching elements.
 */
const findDropAreaNarrows = viewElement => {
  let dropAreaNarrows = []

  // Check the current element itself
  if (viewElement.hasClass && viewElement.hasClass('drop-area-narrow')) {
    dropAreaNarrows.push(viewElement)
  }

  // Recurse through children if it's a container element (like DocumentRoot, div, p, etc.)
  if (
    viewElement.is('containerElement') ||
    viewElement.is('documentFragment')
  ) {
    for (const child of viewElement.getChildren()) {
      dropAreaNarrows = dropAreaNarrows.concat(findDropAreaNarrows(child))
    }
  }

  return dropAreaNarrows
}

/**
 * Replaces or inserts content (embeds, interactive activities, or HTML) into the CKEditor.
 * Prioritizes replacing existing '.drop-area-wide' elements (nested or top-level).
 * If no drop area is found, it inserts content before the editor footer, or at the end of the document.
 *
 * @param {module:editor-classic/classic-editor~ClassicEditor} editor The CKEditor instance.
 * @param {Object} newData Data of the content to be inserted.
 * @param {string} type Category type ('embed', 'actions', 'media', etc.).
 * @param {Function} dispatch Redux dispatch function.
 * @param {Object} Embed Embed utility or class reference.
 * @param {string} fontFamily Current font family setting.
 * @param {string} language Current language setting.
 * @param {HTMLElement} dropTargetElement Optional DOM element that was the drop target.
 */
export const replacedropAreaWideContent = (
  editor,
  newData,
  type,
  dispatch,
  Embed,
  CommunityBoardModal,
  fontFamily,
  language,
  languageCode,
  dropTargetElement = null,
) => {
  // Assuming 'track' is defined globally or imported elsewhere
  track('block_added', {
    category: type === 'media' ? 'video' : type,
    type: newData.name,
  })

  // === Embed Content Handling =================================================
  if (type === 'embed' && newData.value !== 'elevenLabs' && newData.value !== 'scorm') {
    editor.model.change(writer => {
      const viewRoot = editor.editing.view.document.getRoot()
      const dropAreaWides = findDropAreaWides(viewRoot)

      if (dropAreaWides.length > 0) {
        const modeldropAreaWides = dropAreaWides.map(viewElement => {
          const viewPosition = editor.editing.view.createPositionBefore(
            viewElement,
          )
          const modelPosition = editor.editing.mapper.toModelPosition(
            viewPosition,
          )
          return modelPosition.nodeAfter
        })

        modeldropAreaWides.forEach(dropAreaWide => {
          if (dropAreaWide) {
            const range = writer.createRangeOn(dropAreaWide)
            const position = range.start
            const doubleClick = true
            handleAddEmbed(
              newData,
              position,
              editor,
              dispatch,
              Embed,
              writer,
              dropAreaWide,
              doubleClick,
            )
          }
        })
      } else {
        let position
        position = writer.createPositionAt(
          editor.model.document.getRoot(),
          'end',
        )

        const doubleClick = true
        handleAddEmbed(
          newData,
          position,
          editor,
          dispatch,
          Embed,
          writer,
          null,
          doubleClick,
        )
      }
    })
  }

  // === SCORM Embed Handling (no modal) ==========================================
  else if (type === 'embed' && newData.value === 'scorm') {
    editor.model.change(writer => {
      const viewRoot = editor.editing.view.document.getRoot()
      const dropAreaWides = findDropAreaWides(viewRoot)
      const dropAreaNarrows = findDropAreaNarrows(viewRoot)

      const scormConfig = {
        packageId: null,
        title: '',
        entryPointUrl: '',
        width: '100%',
        height: '500px',
        version: '',
        fileName: '',
      }

      const createScormStructure = () => {
        const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
          backgroundColor: '#f0f9ff',
        })
        const simpleBox = writer.createElement('simpleBox')
        const column = writer.createElement('column')
        const scormPackage = writer.createElement('scormPackage', {
          scormConfig: JSON.stringify(scormConfig),
        })
        writer.append(simpleBox, simpleBoxWrapper)
        writer.append(column, simpleBox)
        writer.append(scormPackage, column)
        return simpleBoxWrapper
      }

      // First check for drop-area-wide (double-click)
      if (dropAreaWides.length > 0) {
        const modeldropAreaWides = dropAreaWides.map(viewElement => {
          const viewPosition = editor.editing.view.createPositionBefore(viewElement)
          const modelPosition = editor.editing.mapper.toModelPosition(viewPosition)
          return modelPosition.nodeAfter
        })

        const firstDropArea = modeldropAreaWides[0]
        if (firstDropArea) {
          const range = writer.createRangeOn(firstDropArea)
          const position = range.start
          writer.remove(firstDropArea)
          const scormWrapper = createScormStructure()
          editor.model.insertContent(scormWrapper, position)
        }
      }
      // Then check for drop-area-narrow (drag-drop) — use actual drop target
      else if (dropAreaNarrows.length > 0) {
        let targetDropArea = null

        if (dropTargetElement) {
          for (let i = 0; i < dropAreaNarrows.length; i++) {
            const viewElement = dropAreaNarrows[i]
            try {
              const domElement = editor.editing.view.domConverter.mapViewToDom(
                viewElement,
              )
              if (
                domElement === dropTargetElement ||
                (domElement && domElement.contains && domElement.contains(dropTargetElement))
              ) {
                targetDropArea = viewElement
                break
              }
            } catch (error) {
              // Error mapping view to DOM
            }
          }
        }

        // Fallback: try to find by drag-over class
        if (!targetDropArea) {
          for (let i = dropAreaNarrows.length - 1; i >= 0; i--) {
            const viewElement = dropAreaNarrows[i]
            if (viewElement.hasClass && viewElement.hasClass('drag-over')) {
              targetDropArea = viewElement
              break
            }
          }
        }

        // Last resort: use the first one
        if (!targetDropArea) {
          targetDropArea = dropAreaNarrows[0]
        }

        if (targetDropArea) {
          const viewPosition = editor.editing.view.createPositionBefore(
            targetDropArea,
          )
          const modelPosition = editor.editing.mapper.toModelPosition(viewPosition)
          const modelDropArea = modelPosition.nodeAfter

          if (modelDropArea) {
            const range = writer.createRangeOn(modelDropArea)
            const position = range.start
            const scormWrapper = createScormStructure()
            editor.model.insertContent(scormWrapper, position)
          }
        }
      } else {
        const position = writer.createPositionAt(
          editor.model.document.getRoot(),
          'end',
        )
        const scormWrapper = createScormStructure()
        editor.model.insertContent(scormWrapper, position)
      }

      setTimeout(() => {
        removeAllDropAreas(editor)
      }, 500)
    })
  }

  // === Interactive Activity Handling ============================================
  else if (
    (type === 'actions' || type === 'activities') &&
    newData.value === 'interactiveActivity'
  ) {
    track('interactive_activity_started')
    editor.model.change(writer => {
      const viewRoot = editor.editing.view.document.getRoot()
      const dropAreaWides = findDropAreaWides(viewRoot)
      let position

      if (dropAreaWides.length > 0) {
        const modeldropAreaWides = dropAreaWides.map(viewElement => {
          const viewPosition = editor.editing.view.createPositionBefore(
            viewElement,
          )
          const modelPosition = editor.editing.mapper.toModelPosition(
            viewPosition,
          )
          return modelPosition.nodeAfter
        })

        modeldropAreaWides.forEach(dropAreaWide => {
          if (dropAreaWide) {
            const range = writer.createRangeOn(dropAreaWide)
            const position = range.start
            const doubleClick = true

            const currentUserType = UserType[
              window.CONSTANTS.USER_ROLE_ID
            ]?.trim()

            const freeAccountTypes = [
              'AppSumo (Tier 1)',
              'AppSumo (Tier 2)',
              'AppSumo (Tier 3)',
              'AppSumo (Tier 3A)',
              'AppSumo (Tier 2A)',
              'AppSumo (Tier 1A)',
            ]

            if (freeAccountTypes.includes(currentUserType)) {
              handleAddInterActiveFreeAccount(editor, dispatch)
            } else {
              handleAddInterActive(
                newData,
                position,
                editor,
                dispatch,
                Embed,
                writer,
                null,
                doubleClick,
                fontFamily,
                language,
              )
            }
          }
        })
      } else {
        position = writer.createPositionAt(
          editor.model.document.getRoot(),
          'end',
        )
      }

      const doubleClick = true
      const currentUserType = UserType[window.CONSTANTS.USER_ROLE_ID]?.trim()

      const freeAccountTypes = [
        'AppSumo (Tier 1)',
        'AppSumo (Tier 2)',
        'AppSumo (Tier 3)',
        'AppSumo (Tier 3A)',
        'AppSumo (Tier 2A)',
        'AppSumo (Tier 1A)',
      ]

      if (freeAccountTypes.includes(currentUserType)) {
        handleAddInterActiveFreeAccount(editor, dispatch)
      } else {
        handleAddInterActive(
          newData,
          position,
          editor,
          dispatch,
          Embed,
          writer,
          null,
          doubleClick,
          fontFamily,
          language,
        )
      }
    })
  }

  // === Image Hotspot Handling ============================================
  else if ((type === 'actions' || type === 'image') && newData.value === 'imageHotspot') {
    editor.model.change(writer => {
      const viewRoot = editor.editing.view.document.getRoot()
      const dropAreaWides = findDropAreaWides(viewRoot)
      const dropAreaNarrows = findDropAreaNarrows(viewRoot)

      const imageData = {
        src:
          newData.imageUrl ||
          'https://cdn.minicoursegenerator.com/assets/carousel-and-hotspot/carouselAndHotspotImage.png',
        alt: newData.imageAlt || 'Hotspot Image',
      }

      // First check for drop-area-wide (double-click)
      if (dropAreaWides.length > 0) {
        const modeldropAreaWides = dropAreaWides.map(viewElement => {
          const viewPosition = editor.editing.view.createPositionBefore(
            viewElement,
          )
          const modelPosition = editor.editing.mapper.toModelPosition(
            viewPosition,
          )
          return modelPosition.nodeAfter
        })

        const firstDropArea = modeldropAreaWides[0]
        if (firstDropArea) {
          const range = writer.createRangeOn(firstDropArea)
          const position = range.start

          handleAddImageHotspot(position, editor, imageData, writer, firstDropArea)
        }
      }
      // Then check for drop-area-narrow (drag-drop)
      else if (dropAreaNarrows.length > 0) {
        let targetDropArea = null

        if (dropTargetElement) {
          for (let i = 0; i < dropAreaNarrows.length; i++) {
            const viewElement = dropAreaNarrows[i]
            try {
              const domElement = editor.editing.view.domConverter.mapViewToDom(
                viewElement,
              )
              if (domElement === dropTargetElement) {
                targetDropArea = viewElement
                break
              }
            } catch (error) {
              // Error mapping view to DOM
            }
          }
        }

        // Fallback: try to find by drag-over class
        if (!targetDropArea) {
          for (let i = dropAreaNarrows.length - 1; i >= 0; i--) {
            const viewElement = dropAreaNarrows[i]
            if (viewElement.hasClass && viewElement.hasClass('drag-over')) {
              targetDropArea = viewElement
              break
            }
          }
        }

        // Last resort: use the first one
        if (!targetDropArea) {
          targetDropArea = dropAreaNarrows[0]
        }

        if (targetDropArea) {
          const viewPosition = editor.editing.view.createPositionBefore(
            targetDropArea,
          )
          const modelPosition = editor.editing.mapper.toModelPosition(
            viewPosition,
          )
          const modelDropArea = modelPosition.nodeAfter

          if (modelDropArea) {
            const range = writer.createRangeOn(modelDropArea)
            const position = range.start

            handleAddImageHotspot(position, editor, imageData, writer, modelDropArea)
          }
        }
      } else {
        const position = writer.createPositionAt(
          editor.model.document.getRoot(),
          'end',
        )

        handleAddImageHotspot(position, editor, imageData, writer, null)
      }
    })
  }

  // === Community Block Handling ============================================
  else if (type === 'actions' && newData.value === 'communityBlock') {
    const currentUserType = UserType[window.CONSTANTS.USER_ROLE_ID]?.trim()

    // Account types that HAVE access to Community Board
    const communityAccessTypes = [
      'Pro',
      'Elite',
      'AppSumo (Tier 5)',
      'AppSumo (Tier 6)',
      'AppSumo (Tier 7)',
      'AppSumo (Tier 8)',
      'AppSumo (Tier 9)',
      'Plus',
      'Pro v2',
      'Launch',
      'Essentials',
      'Growth',
      'Scale',
      'Enterprise',
    ]

    if (
      !communityAccessTypes.includes(currentUserType) &&
      !UserType.isTrialEquivalent(currentUserType)
    ) {
      handleAddCommunityBoardFreeAccount(editor, dispatch)
    } else {
      editor.model.change(writer => {
        const viewRoot = editor.editing.view.document.getRoot()
        const dropAreaWides = findDropAreaWides(viewRoot)

        if (dropAreaWides.length > 0) {
          const modeldropAreaWides = dropAreaWides.map(viewElement => {
            const viewPosition = editor.editing.view.createPositionBefore(
              viewElement,
            )
            const modelPosition = editor.editing.mapper.toModelPosition(
              viewPosition,
            )
            return modelPosition.nodeAfter
          })

          const firstDropArea = modeldropAreaWides[0]
          if (firstDropArea) {
            const range = writer.createRangeOn(firstDropArea)
            const position = range.start

            handleAddCommunityBoard(
              position,
              editor,
              dispatch,
              CommunityBoardModal,
              writer,
              firstDropArea,
            )
          }
        } else {
          let position = writer.createPositionAt(
            editor.model.document.getRoot(),
            'end',
          )

          handleAddCommunityBoard(
            position,
            editor,
            dispatch,
            CommunityBoardModal,
            writer,
            null,
          )
        }
      })
    }
  }

  // === Activities Handling (Pixel Quest, Wordle, etc.) ============================================
  else if (
    (type === 'actions' || type === 'activities') &&
    (newData.value === 'pixelQuest' ||
      newData.value === 'wordle' ||
      newData.value === 'riddle' ||
      newData.value === 'jumble' ||
      newData.value === 'crossword' ||
      newData.value === 'wordSearch')
  ) {
    const activityType = newData.type || newData.value

    // Modal collects the section IDs / free-form content; on submit we call the
    // .NET /Activity/generate endpoint, then run the original insertion path
    // that turns the response into a CKEditor block.
    const handleActivityGenerate = async payload => {
      dispatch(messageBoxHideAction('message-box-1'))
      dispatch(processStartAction())

      let activityData = null
      try {
        const result = await generateActivityService(payload)
        activityData = result?.data?.data || null
      } catch (error) {
        activityData = null
      }

      dispatch(processStartEnd())
      editor.model.change(writer => {
        const viewRoot = editor.editing.view.document.getRoot()
        const dropAreaWides = findDropAreaWides(viewRoot)
        const dropAreaNarrows = findDropAreaNarrows(viewRoot)

        // Create activity element based on type
        const createActivityStructure = () => {
          // Generate unique block ID
          const blockId = generateUUID()

          // Determine background color based on activity type
          let backgroundColor = '#020617'
          if (newData.value === 'crossword') backgroundColor = '#EFE8D8'
          if (newData.value === 'riddle') backgroundColor = '#fff'
          if (newData.value === 'wordle') backgroundColor = '#fff'
          if (newData.value === 'jumble') backgroundColor = '#f8fafc'
          if (newData.value === 'wordSearch') backgroundColor = '#fff'

          const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
            blockId: blockId,
            backgroundColor: backgroundColor,
          })
          const simpleBox = writer.createElement('simpleBox', {
            backgroundColor: '#000000',
          })
          const column = writer.createElement('column')

          // For now, only Pixel Quest is implemented
          if (newData.value === 'pixelQuest') {
            const gameConfig = activityData || DEFAULT_PIXEL_QUEST_CONFIG

            // Deep validation: ensure NPCs have proper structure
            const validateNPCs = npcs => {
              if (!Array.isArray(npcs)) return DEFAULT_PIXEL_QUEST_CONFIG.npcs

              return npcs.map((npc, index) => {
                const defaultNpc =
                  DEFAULT_PIXEL_QUEST_CONFIG.npcs[index] ||
                  DEFAULT_PIXEL_QUEST_CONFIG.npcs[0]

                return {
                  id: npc.id || defaultNpc.id,
                  name: npc.name || defaultNpc.name,
                  spriteColor: npc.spriteColor || defaultNpc.spriteColor,
                  position: npc.position || defaultNpc.position,
                  question: npc.question || defaultNpc.question,
                  options: npc.options || defaultNpc.options,
                }
              })
            }

            // Ensure gameConfig has all required fields
            const validatedConfig = {
              title: gameConfig.title || DEFAULT_PIXEL_QUEST_CONFIG.title,
              mapGrid: gameConfig.mapGrid || DEFAULT_PIXEL_QUEST_CONFIG.mapGrid,
              startPosition:
                gameConfig.startPosition ||
                DEFAULT_PIXEL_QUEST_CONFIG.startPosition,
              config: gameConfig.config || DEFAULT_PIXEL_QUEST_CONFIG.config,
              npcs: validateNPCs(gameConfig.npcs),
            }

            const pixelQuest = writer.createElement('pixelQuest', {
              gameConfig: JSON.stringify(validatedConfig),
            })
            writer.append(simpleBox, simpleBoxWrapper)
            writer.append(column, simpleBox)
            writer.append(pixelQuest, column)
          } else if (newData.value === 'riddle') {
            let gameConfig = activityData

            // If no activity data, use default API format
            if (!gameConfig) {
              gameConfig = {
                questTitle: 'Mystery Quest',
                language: languageCode || 'en',
                colors: {
                  primary: '#4f46e5',
                  background: '#020617',
                  cardBackground: '#ffffff',
                  textMain: '#0f172a',
                  textSecondary: '#64748b',
                  accent: '#6366f1',
                  success: '#059669',
                },
                riddles: [
                  {
                    answer: 'Piano',
                    hints: [
                      'I have keys but no locks',
                      'I have space but no room',
                      'You can enter but cannot go inside',
                      'What am I?',
                    ],
                  },
                ],
              }
            } else {
              // API data is already in correct format, just ensure language is set
              if (!gameConfig.language) {
                gameConfig.language = languageCode || 'en'
              }
            }

            const riddle = writer.createElement('riddle', {
              gameConfig: JSON.stringify(gameConfig),
            })
            writer.append(simpleBox, simpleBoxWrapper)
            writer.append(column, simpleBox)
            writer.append(riddle, column)
          } else if (newData.value === 'wordle') {
            let gameConfig = activityData

            // If no activity data, use default API format
            if (!gameConfig) {
              gameConfig = {
                questTitle: 'Word Quest',
                language: languageCode || 'en',
                colors: {
                  primary: '#4f46e5',
                  background: '#ffffff',
                  cardBackground: '#ffffff',
                  textMain: '#0f172a',
                  textSecondary: '#64748b',
                  accent: '#6366f1',
                  success: '#059669',
                },
                words: [
                  {
                    word: 'HELLO',
                    hint: 'A common greeting',
                  },
                ],
              }
            } else {
              // If activity data exists but no language, add it
              if (!gameConfig.language) {
                gameConfig.language = languageCode || 'en'
              }
            }

            const wordle = writer.createElement('wordle', {
              gameConfig: JSON.stringify(gameConfig),
            })
            writer.append(simpleBox, simpleBoxWrapper)
            writer.append(column, simpleBox)
            writer.append(wordle, column)
          } else if (newData.value === 'wordSearch') {
            let gameConfig = activityData

            // If no activity data, use default API format
            if (!gameConfig) {
              gameConfig = {
                config: {
                  language: languageCode || 'en',
                  bgColor: '#ffffff',
                  primaryColor: '#0ea5e9',
                  accentColor: '#10b981',
                  gridBackgroundColor: '#f0f9ff',
                  textColor: '#1e293b',
                  cardBackgroundColor: '#ffffff',
                  wordItemBackgroundColor: '#f8fafc',
                },
                words: ['HELLO', 'WORLD', 'SEARCH', 'PUZZLE'],
              }
            } else {
              // If activity data exists but no language, add it
              if (gameConfig.config && !gameConfig.config.language) {
                gameConfig.config.language = languageCode || 'en'
              }
            }

            const wordSearch = writer.createElement('wordSearch', {
              gameConfig: JSON.stringify(gameConfig),
            })
            writer.append(simpleBox, simpleBoxWrapper)
            writer.append(column, simpleBox)
            writer.append(wordSearch, column)
          } else if (newData.value === 'crossword') {
            const puzzleConfig = activityData || DEFAULT_CROSSWORD_CONFIG

            // Ensure puzzleConfig has all required fields
            const validatedConfig = {
              config: puzzleConfig.config || DEFAULT_CROSSWORD_CONFIG.config,
              words: puzzleConfig.words || DEFAULT_CROSSWORD_CONFIG.words,
            }

            const crosswordPuzzle = writer.createElement('crosswordPuzzle', {
              puzzleConfig: JSON.stringify(validatedConfig),
            })
            writer.append(simpleBox, simpleBoxWrapper)
            writer.append(column, simpleBox)
            writer.append(crosswordPuzzle, column)
          } else if (newData.value === 'jumble') {
            let gameConfig = activityData

            // If no activity data, use default API format
            if (!gameConfig) {
              gameConfig = {
                config: {
                  language: languageCode || 'en',
                  primaryColor: '#015ffb',
                  accentColor: '#10b981',
                  bgColor: '#f8fafc',
                  showAnswer: true,
                },
                words: [
                  {
                    word: 'HELLO',
                    scrambled: 'OLLEH',
                    definition: 'A common greeting used when meeting someone',
                  },
                  {
                    word: 'WORLD',
                    scrambled: 'DLROW',
                    definition: 'The earth and all the people on it',
                  },
                ],
              }
            } else {
              // API data is already in correct format, just ensure language is set
              if (gameConfig.config && !gameConfig.config.language) {
                gameConfig.config.language = languageCode || 'en'
              }
            }

            const jumble = writer.createElement('jumble', {
              gameConfig: JSON.stringify(gameConfig),
            })
            writer.append(simpleBox, simpleBoxWrapper)
            writer.append(column, simpleBox)
            writer.append(jumble, column)
          }

          return simpleBoxWrapper
        }

        // First check for drop-area-wide (double-click)
        if (dropAreaWides.length > 0) {
          const modelDropAreaWides = dropAreaWides.map(viewElement => {
            const viewPosition = editor.editing.view.createPositionBefore(
              viewElement,
            )
            const modelPosition = editor.editing.mapper.toModelPosition(
              viewPosition,
            )
            return modelPosition.nodeAfter
          })

          const firstDropArea = modelDropAreaWides[0]
          if (firstDropArea) {
            const range = writer.createRangeOn(firstDropArea)
            const position = range.start

            writer.remove(firstDropArea)
            const activityWrapper = createActivityStructure()
            editor.model.insertContent(activityWrapper, position)
          }
        }
        // Then check for drop-area-narrow (drag-drop)
        else if (dropAreaNarrows.length > 0) {
          // Find the drop area that was actually targeted
          let targetDropArea = null

          // If we have the actual DOM drop target element, use it to find the correct view element
          if (dropTargetElement) {
            // Find the corresponding view element for the DOM drop target
            for (let i = 0; i < dropAreaNarrows.length; i++) {
              const viewElement = dropAreaNarrows[i]

              // Get the DOM element for this view element
              try {
                const domElement = editor.editing.view.domConverter.mapViewToDom(
                  viewElement,
                )

                // Check if this is our drop target
                if (domElement === dropTargetElement) {
                  targetDropArea = viewElement
                  break
                }
              } catch (error) {
                // Error mapping view to DOM
              }
            }
          }

          // Fallback: try to find by drag-over class
          if (!targetDropArea) {
            for (let i = dropAreaNarrows.length - 1; i >= 0; i--) {
              const viewElement = dropAreaNarrows[i]
              if (viewElement.hasClass && viewElement.hasClass('drag-over')) {
                targetDropArea = viewElement
                break
              }
            }
          }

          // Last resort: use the first one
          if (!targetDropArea) {
            targetDropArea = dropAreaNarrows[0]
          }

          if (targetDropArea) {
            const viewPosition = editor.editing.view.createPositionBefore(
              targetDropArea,
            )
            const modelPosition = editor.editing.mapper.toModelPosition(
              viewPosition,
            )
            const modelDropArea = modelPosition.nodeAfter

            if (modelDropArea) {
              const range = writer.createRangeOn(modelDropArea)
              const position = range.start

              writer.remove(modelDropArea)
              const activityWrapper = createActivityStructure()
              editor.model.insertContent(activityWrapper, position)
            }
          }
        } else {
          const position = writer.createPositionAt(
            editor.model.document.getRoot(),
            'end',
          )

          const activityWrapper = createActivityStructure()
          editor.model.insertContent(activityWrapper, position)
        }

        // Remove drop areas after insertion
        setTimeout(() => {
          removeDropAreaNarrow(editor)
        }, 300)
        setTimeout(() => {
          removeAllDropAreas(editor)
        }, 500)
      })
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          title: '',
          buttons: [],
          component: (
            <AiActivityPopup
              activityType={activityType}
              languageCode={languageCode}
              onSubmit={handleActivityGenerate}
            />
          ),
          callback: () => {},
        }),
      ),
    )
  }

  // === Standard HTML Content Handling (General purpose blocks) ===================
  else {
    editor.model.change(writer => {
      const viewRoot = editor.editing.view.document.getRoot()
      const dropAreaWides = findDropAreaWides(viewRoot)
      const dropAreaNarrows = findDropAreaNarrows(viewRoot)

      const viewFragment = editor.data.processor.toView(newData.html)
      const modelFragment = editor.data.toModel(viewFragment)

      // First check for drop-area-wide (double-click)
      if (dropAreaWides.length > 0) {
        const modeldropAreaWides = dropAreaWides.map(viewElement => {
          const viewPosition = editor.editing.view.createPositionBefore(
            viewElement,
          )
          const modelPosition = editor.editing.mapper.toModelPosition(
            viewPosition,
          )
          return modelPosition.nodeAfter
        })

        modeldropAreaWides.forEach(dropAreaWide => {
          if (dropAreaWide) {
            const range = writer.createRangeOn(dropAreaWide)
            writer.remove(dropAreaWide)
            editor.model.insertContent(modelFragment, range.start)
          }
        })
      }
      // Then check for drop-area-narrow (drag-drop)
      else if (dropAreaNarrows.length > 0) {
        let targetDropArea = null

        // If we have the actual DOM drop target element, use it to find the correct view element
        if (dropTargetElement) {
          for (let i = 0; i < dropAreaNarrows.length; i++) {
            const viewElement = dropAreaNarrows[i]
            try {
              const domElement = editor.editing.view.domConverter.mapViewToDom(
                viewElement,
              )
              if (domElement === dropTargetElement) {
                targetDropArea = viewElement
                break
              }
            } catch (error) {
              // Error mapping view to DOM
            }
          }
        }

        // Fallback: try to find by drag-over class
        if (!targetDropArea) {
          for (let i = dropAreaNarrows.length - 1; i >= 0; i--) {
            const viewElement = dropAreaNarrows[i]
            if (viewElement.hasClass && viewElement.hasClass('drag-over')) {
              targetDropArea = viewElement
              break
            }
          }
        }

        // Last resort: use the first one
        if (!targetDropArea) {
          targetDropArea = dropAreaNarrows[0]
        }

        if (targetDropArea) {
          const viewPosition = editor.editing.view.createPositionBefore(
            targetDropArea,
          )
          const modelPosition = editor.editing.mapper.toModelPosition(
            viewPosition,
          )
          const modelDropArea = modelPosition.nodeAfter

          if (modelDropArea) {
            const range = writer.createRangeOn(modelDropArea)
            const position = range.start
            writer.remove(modelDropArea)
            editor.model.insertContent(modelFragment, position)
          }
        }
      } else {
        const position = writer.createPositionAt(
          editor.model.document.getRoot(),
          'end',
        )
        editor.model.insertContent(modelFragment, position)
      }

      // Remove drop areas after insertion
      setTimeout(() => {
        removeDropAreaNarrow(editor)
      }, 300)
      setTimeout(() => {
        removeAllDropAreas(editor)
      }, 500)
    })
  }
}
